import { Injectable, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { COOKIE_CONSENT_STORAGE_KEY } from 'src/app/core/constants/cookie-consent.constants';
import {
  SESSION_BROADCAST_CHANNEL,
  SESSION_LOGOUT_REASON_KEY,
  SessionLogoutReason,
} from 'src/app/core/constants/session.constants';
import { isLogoutBroadcastMessage } from 'src/app/core/utils/broadcast-message.util';
import { PendoAnalyticsService } from '../pendo/pendo-analytics.service';
import { PartySearchContextService } from '../party-search/party-search-context.service';
import { WelcomePopupStateService } from '../welcome-popup/welcome-popup-state.service';

interface LogoutBroadcastMessage {
  type: 'logout';
  reason?: SessionLogoutReason;
}

export interface AuthLogoutOptions {
  /** Invoked immediately before clearing storage and Auth0 logout (e.g. close menus). */
  beforeLogout?: () => void;
  reason?: SessionLogoutReason;
  /** Suppresses cross-tab logout broadcast when handling a remote logout event. */
  skipBroadcast?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthLogoutService {
  private readonly auth = inject(AuthService);
  private readonly pendoAnalytics = inject(PendoAnalyticsService);
  private readonly partySearchContext = inject(PartySearchContextService);
  private readonly welcomePopupStateService = inject(WelcomePopupStateService);
  private readonly channel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
  private isRemoteLogout = false;

  constructor() {
    this.channel.onmessage = (event: MessageEvent<LogoutBroadcastMessage>) => {
      if (!isLogoutBroadcastMessage(event.data) || this.isRemoteLogout) {
        return;
      }

      this.isRemoteLogout = true;
      this.logout({ skipBroadcast: true, reason: event.data.reason });
      this.isRemoteLogout = false;
    };
  }

  /**
   * Clears local app storage and completes Auth0 logout with redirect to the app origin.
   * Use `beforeLogout` for UI cleanup shared by header, sidebar, and any future entry points.
   */
  logout(options?: AuthLogoutOptions): void {
    options?.beforeLogout?.();
    this.pendoAnalytics.teardown();
    this.clearClientStorage(options?.reason);
    this.welcomePopupStateService.resetOnLogout();

    if (!options?.skipBroadcast) {
      this.channel.postMessage({
        type: 'logout',
        reason: options?.reason,
      } satisfies LogoutBroadcastMessage);
    }

    this.partySearchContext.clear();
    const returnTo = globalThis.location.origin;

    this.auth
      .logout({
        logoutParams: {
          returnTo,
        },
      })
      .subscribe();
  }

  /**
   * Wipes browser storage on logout; preserves the analytics cookie-consent choice (so returning
   * users aren't re-prompted) and the logout reason for the login page message.
   */
  private clearClientStorage(reason?: SessionLogoutReason): void {
    const consent = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);

    localStorage.clear();
    sessionStorage.clear();

    if (consent) {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, consent);
    }

    if (reason) {
      sessionStorage.setItem(SESSION_LOGOUT_REASON_KEY, reason);
    }
  }
}
